import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { PlusCircle, Pencil, Trash2, Search, Loader2, MoreHorizontal, Eye } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import MastersLayout from "@/components/masters/MastersLayout";
import TablePagination from "@/components/masters/TablePagination";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { areaApi, authenticatedApi } from "@/services/api";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from "@/components/ui/card";

// Area type based on API response
type Area = {
  id: string;
  organizationId: string;
  name: string;
  description: string;
  createdAt: string;
  updatedAt: string;
};

type PaginatedResponse = {
  docs: Area[];
  totalDocs: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
};

const ITEMS_PER_PAGE = 10;

const AreasPage = () => {
  const [areas, setAreas] = useState<Area[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState<{
    name: string;
    description: string;
    organizationId: string;
  }>({
    name: "",
    description: "",
    organizationId: "",
  });
  const [editingId, setEditingId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    query: "",
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [areaToDelete, setAreaToDelete] = useState<string | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const { toast } = useToast();

  // Fetch organization ID from localStorage
  useEffect(() => {
    const storedOrg = localStorage.getItem("manterflow-user-profile");
    if (storedOrg) {
      try {
        const orgData = JSON.parse(storedOrg);
        setFormData((prev) => ({
          ...prev,
          organizationId: orgData.organizationId,
        }));
      } catch (e) {
        console.error("Error parsing organization data", e);
      }
    }
  }, []);

  // Fetch areas from API
  const fetchAreas = async () => {
    try {
      setLoading(true);
      const response = await areaApi.getAreas({
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        query: filters.query || undefined,
      });

      if (response && response.docs) {
        setAreas(response.docs);
        setTotalItems(response.totalDocs);
      }
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: (error as Error).message ?? "Failed to fetch areas",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch areas when page or filters change
  useEffect(() => {
    fetchAreas();
  }, [currentPage, filters.query]);

  const handleInputChange = (field: string, value: string) => {
    if (field === "name") {
      // Capitalize first letter of each word
      const capitalizedValue = (value as string)
        .split(" ")
        .map(
          (word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        )
        .join(" ");
      setFormData({ ...formData, [field]: capitalizedValue });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const handleEdit = (area: Area) => {
    setFormData({
      name: area.name.trim(),
      description: area.description.trim(),
      organizationId: area.organizationId,
    });
    setEditingId(area.id);
    setShowForm(true);
  };

  const handleDelete = async (id: string) => {
    setAreaToDelete(id);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!areaToDelete) return;

    try {
      setLoading(true);
      await areaApi.deleteArea(areaToDelete);

      toast({
        title: "Area Deleted",
        description: "The area has been deleted successfully.",
      });

      // Refresh the areas list
      fetchAreas();
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: (error as Error).message ?? "Failed to delete area",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setAreaToDelete(null);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Trim fields before submission
    const trimmedData = {
      ...formData,
      name: formData.name.trim(),
      description: formData.description.trim(),
    };

    if (!trimmedData.name) {
      toast({
        title: "Error",
        description: "Please fill in all required fields",
        variant: "destructive",
      });
      return;
    }

    try {
      setLoading(true);
      if (editingId) {
        await areaApi.updateArea(editingId, trimmedData);
        toast({
          title: "Success",
          description: "Area updated successfully",
        });
      } else {
        await areaApi.createArea(trimmedData);
        toast({
          title: "Success",
          description: "Area added successfully",
        });
      }

      // Refresh the areas list
      fetchAreas();
      setShowForm(false);
    } catch (error: unknown) {
      toast({
        title: "Error",
        description: (error as Error).message ?? "Failed to save area",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  // No need for client-side filtering as the API handles search
  const currentItems = areas;

  // Calculate total pages based on total items from API
  const totalPages = Math.max(1, Math.ceil(totalItems / ITEMS_PER_PAGE));

  // Add this function to handle filter changes
  const handleFilterChange = (field: string, value: string) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setCurrentPage(1); // Reset to first page when filters change
  };

  return (
    <MastersLayout
      // title="Areas"
      // description="Manage your areas"
      // headerAction={
      //   <Button
      //     onClick={() => {
      //       setFormData((prev) => ({
      //         name: "",
      //         description: "",
      //         organizationId: prev.organizationId,
      //       }));
      //       setEditingId(null);
      //       setShowForm(!showForm);
      //     }}
      //   >
      //     <PlusCircle className="mr-2 h-4 w-4" />
      //     Add Area
      //   </Button>
      // }
    >
      <div className="space-y-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search areas..."
                value={filters.query}
                onChange={(e) =>
                  handleFilterChange("query", e.target.value.trim())
                }
                className="pl-8"
              />
            </div>
          </div>
          <Button
            variant="outline"
            onClick={() => {
              setFilters({ query: "" });
              setCurrentPage(1);
            }}
          >
            Reset
          </Button>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Areas</h2>
            <p className="text-muted-foreground">Manage your areas</p>
          </div>
          <Button
            onClick={() => {
              setFormData((prev) => ({
                name: "",
                description: "",
                organizationId: prev.organizationId,
              }));
              setEditingId(null);
              setShowForm(!showForm);
            }}
          >
            <PlusCircle className="mr-2 h-4 w-4" />
            Add Area
          </Button>
        </div>

        {showForm && (
          <Dialog
            open={showForm}
            onOpenChange={(open) => {
              if (!open) {
                setFormData({
                  name: "",
                  description: "",
                  organizationId: formData.organizationId,
                });
                setEditingId(null);
              }
              setShowForm(open);
            }}
          >
            <DialogContent>
              <DialogHeader>
                <DialogTitle>{editingId ? "Edit" : "Add New"} Area</DialogTitle>
                <DialogDescription>
                  {editingId
                    ? "Update the area information below"
                    : "Fill in the area information below"}
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="grid gap-4">
                <div className="grid grid-cols-1 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="name">Area Name</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value.trim())
                      }
                      placeholder="Enter area name"
                      required
                    />
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="description">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          description: e.target.value,
                        })
                      }
                      placeholder="Enter area description"
                      rows={3}
                    />
                  </div>
                </div>
                <DialogFooter>
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setFormData((prev) => ({
                        name: "",
                        description: "",
                        organizationId: prev.organizationId,
                      }));
                      setEditingId(null);
                    }}
                  >
                    Cancel
                  </Button>
                  <Button type="submit">
                    {editingId ? "Update" : "Create"} Area
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}

        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead style={{ minWidth: "200px" }}>Description</TableHead>
              <TableHead className="w-[50px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {currentItems.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={2}
                  className="text-center py-4 text-muted-foreground"
                >
                  {filters.query
                    ? "No areas match the search criteria"
                    : "No areas found"}
                </TableCell>
              </TableRow>
            ) : (
              currentItems.map((area) => (
                <TableRow key={area.id}>
                  <TableCell className="font-medium whitespace-nowrap">
                    {area.name}
                  </TableCell>
                  <TableCell className="w-auto md:w-1/3 lg:w-2/5 overflow-hidden">
                    <div className="break-words whitespace-normal line-clamp-3">
                      {area.description}
                    </div>
                  </TableCell>{" "}
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="cursor-pointer">
                          <MoreHorizontal className="h-4 w-4" />
                          <span className="sr-only">Open menu</span>
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => handleEdit(area)} className="cursor-pointer">
                          <Pencil className="h-4 w-4 mr-2" />
                          Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => handleDelete(area.id)} 
                          className="cursor-pointer text-destructive focus:text-destructive"
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Delete
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {totalItems > ITEMS_PER_PAGE && (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        )}
      </div>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Are you sure?</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure want to delete the area.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </MastersLayout>
  );
};

export default AreasPage;
